import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  Tooltip,
  Card,
  Grid,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Menu,
} from '@mui/material';
import {
  Delete,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  ThumbUp,
  CloudUpload,
  Add,
  Close,
  FileDownload,
  Edit,
  Visibility,
  WbSunny,
  Air,
  Bolt,
  VerifiedUser,
  AttachFile,
  Business,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useDropzone } from 'react-dropzone';
import { usePermissions } from '../../hooks/usePermissions';
import { ApprovalDialog } from '../shared/ApprovalDialog';
import { useApp } from '../../contexts/AuthContext';
import { renewableService, RenewableEntry, RenewableSummary } from '../../services';

interface RenewableData {
  id: string;
  source: string;
  procurementType: string;
  generation: number;
  consumption: number;
  unit: string;
  facility: string;
  facility_id: string;
  period: string;
  certificationType?: string;
  status: string;
  uploadedBy?: string;
  uploadedAt?: string;
  supplier?: string;
  contractId?: string;
  attachmentCount?: number;
}

const RENEWABLE_SOURCES = [
  { value: 'Solar PV', label: 'Solar Photovoltaic', icon: <WbSunny sx={{ fontSize: 16 }} /> },
  { value: 'Wind', label: 'Wind Power', icon: <Air sx={{ fontSize: 16 }} /> },
  { value: 'Hydro', label: 'Hydroelectric', icon: <Bolt sx={{ fontSize: 16 }} /> },
  { value: 'Biomass', label: 'Biomass', icon: <Bolt sx={{ fontSize: 16 }} /> },
  { value: 'Geothermal', label: 'Geothermal', icon: <Bolt sx={{ fontSize: 16 }} /> },
  { value: 'Mixed Renewable', label: 'Mixed Renewable', icon: <Bolt sx={{ fontSize: 16 }} /> },
];

// Procurement types for reference - On-site vs Procurement tabs
// 'On-site': ['On-site Generation']
// 'Procurement': ['Power Purchase Agreement (PPA)', 'Green Tariff', 'Unbundled EACs']

const CERTIFICATION_TYPES = [
  { value: 'Guarantee of Origin (GO)', label: 'GO (Guarantee of Origin)' },
  { value: 'I-REC', label: 'I-REC' },
  { value: 'REC', label: 'REC (US)' },
  { value: 'REGO', label: 'REGO (UK)' },
  { value: 'None', label: 'None' },
];

export const RenewableEnergyCollection: React.FC = () => {
  const { user, selectedPeriod, facilities } = useApp();
  const permissions = usePermissions();
  const [renewableData, setRenewableData] = useState<RenewableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<RenewableData | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0: On-site, 1: Procurement
  const [editMode, setEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState<RenewableData | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [facilityFilter, setFacilityFilter] = useState<string>('all');

  // Summary state
  const [summary, setSummary] = useState<RenewableSummary | null>(null);

  const [formData, setFormData] = useState({
    source: '',
    procurementType: '',
    generation: '',
    consumption: '',
    unit: 'MWh',
    facility_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    certificationType: 'None',
    supplier: '',
    contractId: '',
    notes: '',
  });

  // Facility filter options
  const facilityOptions = [
    { value: 'all', label: 'All Facilities' },
    ...facilities.map(f => ({ value: f.id, label: f.name }))
  ];

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entriesResponse, summaryResponse] = await Promise.all([
        renewableService.listEntries({ period: selectedPeriod?.year?.toString() }),
        renewableService.getSummary(selectedPeriod?.year?.toString()),
      ]);

      // Transform API response to component format
      const transformedData: RenewableData[] = (entriesResponse.entries || []).map((entry: RenewableEntry) => ({
        id: entry.id,
        source: entry.source,
        procurementType: entry.procurement_type,
        generation: entry.generation_mwh || 0,
        consumption: entry.consumption_mwh,
        unit: 'MWh',
        facility: entry.facility_name,
        facility_id: entry.facility_id,
        period: entry.reporting_period,
        certificationType: entry.certification_type !== 'None' ? entry.certification_type : undefined,
        status: entry.status,
        uploadedBy: entry.created_by,
        uploadedAt: entry.created_at,
        supplier: (entry as any).supplier,
        contractId: (entry as any).contract_id,
        attachmentCount: (entry as any).attachment_count || 0,
      }));

      setRenewableData(transformedData);
      setSummary(summaryResponse);
    } catch (err: any) {
      setError(err.message || 'Failed to load renewable energy data');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod?.year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when switching tabs
  useEffect(() => {
    setPage(0);
    setFacilityFilter('all');
  }, [activeTab]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: (files) => {
      console.log('Files uploaded:', files);
      setUploadDialogOpen(false);
    },
  });

  // Get procurement category for current tab
  const getProcurementForTab = (tab: number): string => {
    return tab === 0 ? 'On-site' : 'Procurement';
  };

  // Filter data by tab and facility
  const getFilteredData = (): RenewableData[] => {
    let data = renewableData;

    // Filter by tab (On-site vs Procurement)
    if (activeTab === 0) {
      data = data.filter(d => d.procurementType === 'On-site Generation');
    } else {
      data = data.filter(d => d.procurementType !== 'On-site Generation');
    }

    // Filter by facility
    if (facilityFilter !== 'all') {
      data = data.filter(d => d.facility_id === facilityFilter);
    }

    // Filter by permissions
    if (permissions.userRole === 'Contributor') {
      data = data.filter(item => item.uploadedBy === user?.name || item.status === 'approved');
    } else if (permissions.userRole !== 'Admin') {
      data = data.filter(item => item.status === 'approved');
    }

    return data;
  };

  const filteredData = getFilteredData();

  // Pagination
  const getCurrentPageData = (data: RenewableData[]) => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Calculate summary stats
  const totalGeneration = summary?.total_generation_mwh || 0;
  const totalConsumption = summary?.total_consumption_mwh || 0;
  const certificatesCount = summary?.certificates_count || 0;
  // const onsiteCapacity = summary?.onsite_capacity_mw || 0; // Available for future use
  const approvedCount = summary?.approved_count || 0;
  const pendingCount = summary?.pending_count || 0;

  // Chart data - aggregate by month (only approved entries)
  const getChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const chartData = months.map((month, idx) => {
      // Only include approved entries in chart
      const monthData = renewableData.filter(d => {
        const [, m] = d.period?.split('-') || [];
        return parseInt(m) === idx + 1 && d.status === 'approved';
      });

      const onsite = monthData.filter(d => d.procurementType === 'On-site Generation').reduce((sum, d) => sum + d.consumption, 0);
      const ppa = monthData.filter(d => d.procurementType.includes('PPA')).reduce((sum, d) => sum + d.consumption, 0);
      const other = monthData.filter(d => !d.procurementType.includes('PPA') && d.procurementType !== 'On-site Generation').reduce((sum, d) => sum + d.consumption, 0);

      return {
        month,
        'On-site': onsite,
        'PPA': ppa,
        'Other': other,
      };
    });
    return chartData;
  };

  const chartData = getChartData();

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { icon: React.ReactElement; color: string; bg: string; label: string }> = {
      approved: { icon: <CheckCircle sx={{ fontSize: 16 }} />, color: '#059669', bg: '#ecfdf5', label: 'Approved' },
      submitted: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      pending: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      draft: { icon: <Edit sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Draft' },
      rejected: { icon: <ErrorIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Rejected' },
    };
    return configs[status] || configs.draft;
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'Solar PV': return <WbSunny sx={{ fontSize: 16, color: '#f59e0b' }} />;
      case 'Wind': return <Air sx={{ fontSize: 16, color: '#0ea5e9' }} />;
      default: return <Bolt sx={{ fontSize: 16, color: '#059669' }} />;
    }
  };

  const handleApprove = async (entryId: string, comment?: string) => {
    try {
      await renewableService.approveEntry(entryId, comment);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to approve entry');
    }
  };

  const handleReject = async (entryId: string, reason: string) => {
    try {
      await renewableService.rejectEntry(entryId, reason);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to reject entry');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await renewableService.deleteEntry(entryId);
        await fetchData();
      } catch (err: any) {
        setError(err.message || 'Failed to delete entry');
      }
    }
  };

  // Convert energy value to MWh based on selected unit
  const convertToMWh = (value: number, unit: string): number => {
    switch (unit) {
      case 'kWh': return value / 1000;
      case 'GWh': return value * 1000;
      case 'MWh':
      default: return value;
    }
  };

  // Handle edit - open dialog with entry data
  const handleEdit = (entry: RenewableData) => {
    setEditMode(true);
    setEditingEntry(entry);
    // Parse period to get year and month
    const [year, month] = entry.period?.split('-').map(Number) || [new Date().getFullYear(), new Date().getMonth() + 1];
    setFormData({
      source: entry.source,
      procurementType: entry.procurementType,
      generation: entry.generation?.toString() || '',
      consumption: entry.consumption?.toString() || '',
      unit: 'MWh',
      facility_id: entry.facility_id || '',
      year: year,
      month: month,
      certificationType: entry.certificationType || 'None',
      supplier: entry.supplier || '',
      contractId: entry.contractId || '',
      notes: '',
    });
    setEntryDialogOpen(true);
  };

  const handleSubmitEntry = async () => {
    try {
      const generationValue = parseFloat(formData.generation) || 0;
      const consumptionValue = parseFloat(formData.consumption) || 0;

      // Convert to MWh for storage
      const generationMWh = generationValue > 0 ? convertToMWh(generationValue, formData.unit) : undefined;
      const consumptionMWh = convertToMWh(consumptionValue, formData.unit);

      const entryData = {
        source: formData.source as any,
        procurement_type: formData.procurementType as any,
        generation_mwh: generationMWh,
        consumption_mwh: consumptionMWh,
        certification_type: formData.certificationType as any || 'None',
        facility_id: formData.facility_id || undefined,
        year: formData.year,
        month: formData.month,
        supplier: formData.supplier || undefined,
        contract_id: formData.contractId || undefined,
        notes: formData.notes || undefined,
      };

      if (editMode && editingEntry) {
        await renewableService.updateEntry(editingEntry.id, entryData);
      } else {
        await renewableService.createEntry(entryData);
      }
      await fetchData();
      setEntryDialogOpen(false);
      setEditMode(false);
      setEditingEntry(null);
      resetFormData();
    } catch (err: any) {
      setError(err.message || 'Failed to save entry');
    }
  };

  const resetFormData = () => {
    setFormData({
      source: '',
      procurementType: '',
      generation: '',
      consumption: '',
      unit: 'MWh',
      facility_id: '',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      certificationType: 'None',
      supplier: '',
      contractId: '',
      notes: '',
    });
  };

  // Export menu state (Admin only)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExportMenuAnchor(null);
    if (filteredData.length === 0) return;

    setExporting(true);
    try {
      const period = selectedPeriod ? `${selectedPeriod.year}` : undefined;
      await renewableService.exportData(format, period);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards - Aligned with Water/Waste theme */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#fef3c7', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <WbSunny sx={{ color: '#f59e0b' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Total Renewable
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {totalConsumption.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>MWh</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#dbeafe', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Air sx={{ color: '#2563eb' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  On-site Generation
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#2563eb', lineHeight: 1.2 }}>
                  {totalGeneration.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>MWh</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#d1fae5', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <VerifiedUser sx={{ color: '#059669' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Certificates
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#059669', lineHeight: 1.2 }}>
                  {certificatesCount}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>RE100 / EACs</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#ecfdf5', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <CheckCircle sx={{ color: '#059669' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Data Status
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {approvedCount} / {approvedCount + pendingCount}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
                  {pendingCount > 0 ? `${pendingCount} pending` : 'All approved'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Trend Chart */}
      <Card sx={{ mb: 3, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827' }}>
            Renewable Energy Trend ({selectedPeriod?.year || new Date().getFullYear()})
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Monthly breakdown by procurement type (approved data only)
          </Typography>
        </Box>
        <Box sx={{ p: 2, height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <RechartsTooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                formatter={(value) => [`${(value as number).toLocaleString()} MWh`, '']}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="On-site" stackId="1" stroke="#2563eb" fill="#dbeafe" name="On-site Generation" />
              <Area type="monotone" dataKey="PPA" stackId="1" stroke="#059669" fill="#d1fae5" name="PPA" />
              <Area type="monotone" dataKey="Other" stackId="1" stroke="#f59e0b" fill="#fef3c7" name="Green Tariff / EACs" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Card>

      {/* Data Table Card */}
      <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
        {/* Tabs and Actions */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          borderBottom: '1px solid #e5e7eb',
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#6b7280',
                minHeight: 48,
                px: 2,
              },
              '& .Mui-selected': {
                color: '#111827',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#059669',
                height: 2,
              },
            }}
          >
            <Tab label="On-site Generation" icon={<WbSunny sx={{ fontSize: 16, ml: 0.5 }} />} iconPosition="end" />
            <Tab label="Procurement" icon={<VerifiedUser sx={{ fontSize: 16, ml: 0.5 }} />} iconPosition="end" />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {permissions.canCreate && (
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 18 }} />}
                onClick={() => {
                  const procType = activeTab === 0 ? 'On-site Generation' : '';
                  setFormData(prev => ({ ...prev, procurementType: procType }));
                  setEntryDialogOpen(true);
                }}
                sx={{
                  backgroundColor: '#059669',
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  '&:hover': { backgroundColor: '#047857', boxShadow: 'none' },
                }}
              >
                Add Entry
              </Button>
            )}
            {/* Export Button - Admin Only */}
            {permissions.userRole === 'Admin' && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: 16 }} />}
                  disabled={filteredData.length === 0 || exporting}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  sx={{
                    borderColor: '#e5e7eb',
                    color: '#6b7280',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' },
                    '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' },
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={() => setExportMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Filter Bar */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Facility</InputLabel>
            <Select
              value={facilityFilter}
              label="Facility"
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setPage(0);
              }}
              sx={{ backgroundColor: '#fff', fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              {facilityOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {facilityFilter !== 'all' && (
            <Button
              size="small"
              onClick={() => {
                setFacilityFilter('all');
                setPage(0);
              }}
              sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
            >
              Clear Filter
            </Button>
          )}

          <Typography sx={{ color: '#9ca3af', ml: 'auto', fontSize: '0.75rem' }}>
            {filteredData.length} entries
          </Typography>
        </Box>

        {/* Data Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Procurement Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Certification</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Facility</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Evidence</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem', width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 6, textAlign: 'center' }}>
                    <WbSunny sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: '#6b7280', mb: 0.5 }}>No {activeTab === 0 ? 'on-site generation' : 'procurement'} data yet</Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>Click "Add Entry" to start tracking renewable energy</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getCurrentPageData(filteredData).map((row) => {
                  const status = getStatusConfig(row.status);
                  const actions = permissions.getEntryActions(row.status, row.uploadedBy);

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f9fafb' },
                        '& td': { py: 2, borderBottom: '1px solid #f3f4f6' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getSourceIcon(row.source)}
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                            {row.source}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.procurementType}
                          size="small"
                          sx={{
                            backgroundColor: row.procurementType === 'On-site Generation' ? '#dbeafe' : row.procurementType.includes('PPA') ? '#d1fae5' : '#f3f4f6',
                            color: row.procurementType === 'On-site Generation' ? '#2563eb' : row.procurementType.includes('PPA') ? '#059669' : '#4b5563',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {row.consumption.toLocaleString()} {row.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.certificationType ? (
                          <Chip
                            label={row.certificationType}
                            size="small"
                            sx={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>None</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.facility || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.period ? (() => {
                            const [year, month] = row.period.split('-');
                            return month && year
                              ? `${new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'short' })} ${year}`
                              : row.period;
                          })() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.uploadedBy ? row.uploadedBy.split('@')[0].split('.').map((n: string) => n.charAt(0).toUpperCase() + n.slice(1)).join(' ') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 24,
                            backgroundColor: status.bg,
                            color: status.color,
                            fontWeight: 500,
                            '& .MuiChip-icon': { color: status.color },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.attachmentCount && row.attachmentCount > 0 ? (
                          <Tooltip title={`${row.attachmentCount} file${row.attachmentCount !== 1 ? 's' : ''} attached`}>
                            <Chip
                              icon={<AttachFile sx={{ fontSize: 14 }} />}
                              label={row.attachmentCount}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 22,
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                fontWeight: 500,
                                '& .MuiChip-icon': { color: '#6b7280' },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {/* Edit button - Admin only */}
                          {permissions.userRole === 'Admin' && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#2563eb', '&:hover': { backgroundColor: '#dbeafe' } }}>
                                <Edit sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canApprove && (
                            <Tooltip title="Approve">
                              <IconButton size="small" onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }} sx={{ color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}>
                                <ThumbUp sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: '#6b7280', '&:hover': { backgroundColor: '#f3f4f6' } }}>
                                <Delete sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!actions.canApprove && !actions.canDelete && permissions.userRole !== 'Admin' && (
                            <Tooltip title="View Only">
                              <IconButton size="small" disabled sx={{ color: '#d1d5db' }}><Visibility sx={{ fontSize: 18 }} /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
            }}
          />
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Upload Renewable Energy Data</Typography>
          <IconButton onClick={() => setUploadDialogOpen(false)} size="small"><Close /></IconButton>
        </Box>
        <DialogContent>
          <Box {...getRootProps()} sx={{ border: '2px dashed', borderColor: isDragActive ? '#059669' : '#d1d5db', borderRadius: 3, p: 5, textAlign: 'center', backgroundColor: isDragActive ? '#f0fdf4' : '#fafafa', cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: '#059669', backgroundColor: '#f0fdf4' } }}>
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 56, color: isDragActive ? '#059669' : '#9ca3af', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>{isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>or click to browse</Typography>
            <Chip label="Accepts Excel (.xlsx) or CSV files" size="small" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog
        open={entryDialogOpen}
        onClose={() => { setEntryDialogOpen(false); setEditMode(false); setEditingEntry(null); resetFormData(); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', maxHeight: '85vh' } }}
      >
        <Box sx={{
          px: 2, py: 1.5,
          backgroundColor: editMode ? '#f59e0b' : '#059669',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem' }}>
              {editMode ? 'Edit Renewable Energy' : 'Add Renewable Energy'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
              RE100 & GHG Protocol Scope 2 compliant
            </Typography>
          </Box>
          <IconButton onClick={() => { setEntryDialogOpen(false); setEditMode(false); setEditingEntry(null); resetFormData(); }} size="small" sx={{ color: '#fff' }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Facility Selection */}
            <FormControl fullWidth size="small">
              <InputLabel shrink sx={{ fontSize: '0.8rem' }}>Facility</InputLabel>
              <Select
                value={formData.facility_id}
                label="Facility"
                onChange={(e) => setFormData({ ...formData, facility_id: e.target.value })}
                sx={{ fontSize: '0.8rem' }}
                displayEmpty
                notched
                renderValue={(value) => {
                  if (value === '') {
                    return 'Organization-wide';
                  }
                  const facility = facilities.find(f => f.id === value);
                  return facility?.name || value;
                }}
              >
                {/* Organization-wide option */}
                <MenuItem value="" sx={{ fontSize: '0.8rem', py: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: '#6b7280' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>Organization-wide</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Not facility-specific</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {facilities.length === 0 ? (
                  <MenuItem disabled value="no-facilities">No facilities available</MenuItem>
                ) : (
                  facilities.map((f) => (
                    <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{f.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{f.type} • {f.country}</Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Reporting Period */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Year</InputLabel>
                <Select
                  value={formData.year}
                  label="Year"
                  onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem', py: 0.5 }}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Month</InputLabel>
                <Select
                  value={formData.month}
                  label="Month"
                  onChange={(e) => setFormData({ ...formData, month: Number(e.target.value) })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[
                    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
                    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
                    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
                    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
                  ].map((month) => (
                    <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Source Selection */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Renewable Source</InputLabel>
              <Select
                value={formData.source}
                label="Renewable Source"
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                sx={{ fontSize: '0.8rem' }}
              >
                {RENEWABLE_SOURCES.map((src) => (
                  <MenuItem key={src.value} value={src.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{src.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Procurement Type */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Procurement Type</InputLabel>
              <Select
                value={formData.procurementType}
                label="Procurement Type"
                onChange={(e) => setFormData({ ...formData, procurementType: e.target.value })}
                sx={{ fontSize: '0.8rem' }}
              >
                <MenuItem value="On-site Generation" sx={{ fontSize: '0.8rem', py: 0.5 }}>On-site Generation</MenuItem>
                <MenuItem value="Power Purchase Agreement (PPA)" sx={{ fontSize: '0.8rem', py: 0.5 }}>Power Purchase Agreement (PPA)</MenuItem>
                <MenuItem value="Green Tariff" sx={{ fontSize: '0.8rem', py: 0.5 }}>Green Tariff</MenuItem>
                <MenuItem value="Unbundled EACs" sx={{ fontSize: '0.8rem', py: 0.5 }}>Unbundled EACs/RECs</MenuItem>
              </Select>
            </FormControl>

            {/* Certification Type (for non-onsite) */}
            {formData.procurementType && formData.procurementType !== 'On-site Generation' && (
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Certification Type</InputLabel>
                <Select
                  value={formData.certificationType}
                  label="Certification Type"
                  onChange={(e) => setFormData({ ...formData, certificationType: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {CERTIFICATION_TYPES.map((cert) => (
                    <MenuItem key={cert.value} value={cert.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{cert.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Energy Amount with Unit Selection */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                label={formData.procurementType === 'On-site Generation' ? 'Generation Amount' : 'Consumption Amount'}
                type="number"
                size="small"
                fullWidth
                required
                value={formData.procurementType === 'On-site Generation' ? formData.generation : formData.consumption}
                onChange={(e) => {
                  if (formData.procurementType === 'On-site Generation') {
                    setFormData({ ...formData, generation: e.target.value, consumption: e.target.value });
                  } else {
                    setFormData({ ...formData, consumption: e.target.value });
                  }
                }}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                placeholder="Enter amount"
              />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Unit</InputLabel>
                <Select
                  value={formData.unit}
                  label="Unit"
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="kWh" sx={{ fontSize: '0.8rem', py: 0.5 }}>kWh</MenuItem>
                  <MenuItem value="MWh" sx={{ fontSize: '0.8rem', py: 0.5 }}>MWh</MenuItem>
                  <MenuItem value="GWh" sx={{ fontSize: '0.8rem', py: 0.5 }}>GWh</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Supplier / Contract ID for PPAs */}
            {formData.procurementType === 'Power Purchase Agreement (PPA)' && (
              <>
                <TextField
                  label="Supplier / Generator"
                  size="small"
                  fullWidth
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                  InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                  placeholder="e.g., SunPower Corp"
                />
                <TextField
                  label="Contract ID (Optional)"
                  size="small"
                  fullWidth
                  value={formData.contractId}
                  onChange={(e) => setFormData({ ...formData, contractId: e.target.value })}
                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                  InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                  placeholder="e.g., PPA-2025-001"
                />
              </>
            )}

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              InputProps={{ sx: { fontSize: '0.8rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
              placeholder="Add any relevant notes..."
            />
          </Box>
        </DialogContent>

        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <Button size="small" onClick={() => { setEntryDialogOpen(false); setEditMode(false); setEditingEntry(null); resetFormData(); }} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmitEntry}
            disabled={!formData.source || !formData.procurementType || (!formData.consumption && !formData.generation)}
            sx={{
              backgroundColor: editMode ? '#f59e0b' : '#059669',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              boxShadow: 'none',
              '&:hover': { backgroundColor: editMode ? '#d97706' : '#047857', boxShadow: 'none' },
              '&:disabled': { backgroundColor: '#e5e7eb' },
            }}
          >
            {editMode ? 'Update Entry' : 'Submit for Approval'}
          </Button>
        </Box>
      </Dialog>

      {/* Approval Dialog */}
      {selectedEntry && (
        <ApprovalDialog
          open={approvalDialogOpen}
          onClose={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onApprove={(comment) => { handleApprove(selectedEntry.id, comment); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onReject={(reason) => { handleReject(selectedEntry.id, reason); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          entryData={{ category: selectedEntry.source, value: selectedEntry.consumption, unit: selectedEntry.unit, uploadedBy: selectedEntry.uploadedBy, uploadedAt: selectedEntry.uploadedAt }}
        />
      )}
    </Box>
  );
};
